/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { parseJSONSafely } from "utils/misc"
import { storageHelper } from "utils/storage"

/**
 * Number of counted (non-`GENERAL`) events required before the feedback prompt
 * is shown for a given user.
 */
const PMF_FEEDBACK_SHOW_THRESHOLD = 3

/** Inclusive max post-increment `count` at which the counted-event prompt may open. */
const PMF_FEEDBACK_SHOW_COUNT_MAX = 4

export interface PmfFeedbackUserState {
  count: number
  done: boolean
}

/** Per-user localStorage map key; sourced from `user_profile.grab_id` today. */
export type PmfFeedbackUserIdentifier = string

export type PmfFeedbackState = Record<
  PmfFeedbackUserIdentifier,
  PmfFeedbackUserState
>

const DEFAULT_USER_STATE: PmfFeedbackUserState = { count: 0, done: false }

const shouldShowFeedback = (state: PmfFeedbackUserState): boolean =>
  !state.done &&
  state.count >= PMF_FEEDBACK_SHOW_THRESHOLD &&
  state.count <= PMF_FEEDBACK_SHOW_COUNT_MAX

/**
 * Identifier for the logged-in user in PMF localStorage. Currently
 * `user_profile.grab_id`. Returns an empty string when the profile or id is missing.
 */
export const getPmfFeedbackUserIdentifier = (): PmfFeedbackUserIdentifier =>
  storageHelper.getUserProfile()?.user_profile?.grab_id ?? ""

/**
 * Read the entire PMF feedback state blob from localStorage. Returns an empty
 * object when the key is missing or the stored value is unparseable.
 */
export const readPmfFeedbackState = (): PmfFeedbackState =>
  parseJSONSafely<PmfFeedbackState>(storageHelper.getPmfFeedbackEventState()) ||
  {}

/**
 * Persist the entire PMF feedback state blob to localStorage.
 */
export const writePmfFeedbackState = (state: PmfFeedbackState): void => {
  storageHelper.setPmfFeedbackEventState(JSON.stringify(state))
}

/**
 * Increment the counted-event counter for a user. No-op (without writing) when
 * the user is already marked done, when `count` is already at
 * `PMF_FEEDBACK_SHOW_COUNT_MAX`, or when `userIdentifier` is empty. Otherwise
 * bumps `count` by 1 and persists. `showFeedback` in the result is true only
 * when the increment lands in the show window (threshold through max
 * inclusive); it is always false on a no-op.
 */
export const incrementPmfFeedbackUserCount = (
  userIdentifier: PmfFeedbackUserIdentifier,
): {
  updatedState: PmfFeedbackUserState
  didIncrement: boolean
  showFeedback: boolean
} => {
  if (!userIdentifier) {
    return {
      updatedState: DEFAULT_USER_STATE,
      didIncrement: false,
      showFeedback: false,
    }
  }

  const state = readPmfFeedbackState()
  const current = state[userIdentifier] ?? DEFAULT_USER_STATE
  if (current.done || current.count >= PMF_FEEDBACK_SHOW_COUNT_MAX) {
    return {
      updatedState: current,
      didIncrement: false,
      showFeedback: false,
    }
  }

  const updated: PmfFeedbackUserState = { ...current, count: current.count + 1 }
  writePmfFeedbackState({ ...state, [userIdentifier]: updated })
  return {
    updatedState: updated,
    didIncrement: true,
    showFeedback: shouldShowFeedback(updated),
  }
}

/**
 * Mark a user as having submitted feedback. Once done, the user will not be
 * prompted again and the counter will not increment further. No-op when
 * `userIdentifier` is empty.
 */
export const markPmfFeedbackUserDone = (
  userIdentifier: PmfFeedbackUserIdentifier,
): void => {
  if (!userIdentifier) return

  const state = readPmfFeedbackState()
  const current = state[userIdentifier] ?? DEFAULT_USER_STATE
  if (current.done) return

  writePmfFeedbackState({
    ...state,
    [userIdentifier]: { ...current, done: true },
  })
}
